import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useAuth } from '../../hooks/useAuth';
import api from '../../utils/api';
import {
  ChartBarIcon,
  ArrowTrendingUpIcon,
  QrCodeIcon,
  ArchiveBoxIcon,
  ListBulletIcon,
  ArrowDownOnSquareIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline';

const DashboardPage = () => {
  const { user } = useAuth();

  // Fetch statistics
  const { data: stats, isLoading: statsLoading } = useQuery('dashboardStats', async () => {
    try {
      const response = await api.get('/api/dashboard/stats');
      return response.data;
    } catch (err) {
      console.error('Failed to fetch dashboard stats:', err);
      return {
        totalItems: 0,
        recentItems: [],
        listCounts: [],
        activity: []
      };
    }
  });

  // Quick actions to display on the dashboard
  const quickActions = [
    {
      name: 'Scan Item',
      description: 'Add items using barcode or camera',
      icon: QrCodeIcon,
      href: '/app/scanner',
      iconForeground: 'text-primary-700',
      iconBackground: 'bg-primary-50'
    },
    {
      name: 'View Inventory',
      description: 'Browse and manage your inventory',
      icon: ArchiveBoxIcon,
      href: '/app/inventory',
      iconForeground: 'text-indigo-700',
      iconBackground: 'bg-indigo-50'
    },
    {
      name: 'Manage Lists',
      description: 'Create and edit inventory lists',
      icon: ListBulletIcon,
      href: '/app/lists',
      iconForeground: 'text-yellow-700',
      iconBackground: 'bg-yellow-50'
    },
    {
      name: 'Export Data',
      description: 'Export inventory to spreadsheets',
      icon: ArrowDownOnSquareIcon,
      href: '/app/export',
      iconForeground: 'text-green-700',
      iconBackground: 'bg-green-50'
    },
    {
      name: 'Consignors',
      description: 'Manage your consignors',
      icon: UserGroupIcon,
      href: '/app/consignors',
      iconForeground: 'text-purple-700',
      iconBackground: 'bg-purple-50'
    }
  ];

  // Statistic cards to show on the dashboard
  const statCards = [
    { name: 'Total Items', value: statsLoading ? '...' : stats?.totalItems || 0, icon: ArchiveBoxIcon, change: '+4.75%', trend: 'up' },
    { name: 'Active Lists', value: statsLoading ? '...' : stats?.listCounts?.length || 0, icon: ListBulletIcon, change: '+10.18%', trend: 'up' },
    { name: 'Items Scanned Today', value: statsLoading ? '...' : stats?.todayScanned || 0, icon: QrCodeIcon, change: '+2.02%', trend: 'up' },
  ];

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
        <p className="mt-1 text-sm text-gray-500">
          Welcome back, {user?.firstName}. Here's an overview of your inventory.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 mb-8">
        {statCards.map((stat) => (
          <div key={stat.name} className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-primary-100 rounded-md p-3">
                  <stat.icon className="h-6 w-6 text-primary-600" aria-hidden="true" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">{stat.name}</dt>
                    <dd>
                      <div className="text-lg font-medium text-gray-900">{stat.value}</div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-5 py-3">
              <div className="text-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center text-sm">
                    {stat.trend === 'up' ? (
                      <ArrowTrendingUpIcon className="mr-1.5 h-4 w-4 flex-shrink-0 text-green-500" />
                    ) : (
                      <ArrowTrendingUpIcon className="mr-1.5 h-4 w-4 flex-shrink-0 text-red-500" />
                    )}
                    <span className={`font-medium ${stat.trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
                      {stat.change}
                    </span>
                    <span className="text-gray-500 ml-1">from last month</span>
                  </div>
                  <Link to="/app/inventory" className="font-medium text-primary-600 hover:text-primary-500">
                    View all
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick actions */}
      <div className="mb-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {quickActions.map((action) => (
            <div
              key={action.name}
              className="relative flex items-center space-x-3 rounded-lg border border-gray-300 bg-white px-6 py-5 shadow-sm focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2 hover:border-gray-400"
            >
              <div className={`flex-shrink-0 ${action.iconBackground} rounded-md p-2`}>
                <action.icon className={`h-6 w-6 ${action.iconForeground}`} aria-hidden="true" />
              </div>
              <div className="min-w-0 flex-1">
                <Link to={action.href} className="focus:outline-none">
                  <span className="absolute inset-0" aria-hidden="true" />
                  <p className="text-sm font-medium text-gray-900">{action.name}</p>
                  <p className="text-xs text-gray-500">{action.description}</p>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Recent Activity */}
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h2>
          <div className="overflow-hidden bg-white shadow sm:rounded-md">
            <ul role="list" className="divide-y divide-gray-200">
              {statsLoading ? (
                <li className="p-4 text-center text-gray-500">Loading activity...</li>
              ) : stats?.activity && stats.activity.length > 0 ? (
                stats.activity.map((activity) => (
                  <li key={activity.id}>
                    <div className="block hover:bg-gray-50">
                      <div className="px-4 py-4 sm:px-6">
                        <div className="flex items-center justify-between">
                          <p className="truncate text-sm font-medium text-primary-600">{activity.action}</p>
                          <div className="ml-2 flex flex-shrink-0">
                            <p className="inline-flex rounded-full bg-green-100 px-2 text-xs font-semibold leading-5 text-green-800">
                              {activity.itemType}
                            </p>
                          </div>
                        </div>
                        <div className="mt-2 sm:flex sm:justify-between">
                          <div className="sm:flex">
                            <p className="flex items-center text-sm text-gray-500">
                              <UserGroupIcon className="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                              {activity.user}
                            </p>
                          </div>
                          <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                            <ChartBarIcon className="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                            <p>
                              <time dateTime={activity.datetime}>{activity.date}</time>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))
              ) : (
                <li className="p-4 text-center text-gray-500">No recent activity</li>
              )}
            </ul>
          </div>
        </div>

        {/* Recent Items */}
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4">Recently Added Items</h2>
          <div className="overflow-hidden bg-white shadow sm:rounded-md">
            <ul role="list" className="divide-y divide-gray-200">
              {statsLoading ? (
                <li className="p-4 text-center text-gray-500">Loading items...</li>
              ) : stats?.recentItems && stats.recentItems.length > 0 ? (
                stats.recentItems.map((item) => (
                  <li key={item.id}>
                    <Link to={`/app/inventory/${item.id}`} className="block hover:bg-gray-50">
                      <div className="px-4 py-4 sm:px-6">
                        <div className="flex items-center justify-between">
                          <p className="truncate text-sm font-medium text-primary-600">{item.title}</p>
                          <div className="ml-2 flex flex-shrink-0">
                            <p className="inline-flex rounded-full bg-blue-100 px-2 text-xs font-semibold leading-5 text-blue-800">
                              {item.listName || 'No List'}
                            </p>
                          </div>
                        </div>
                        <div className="mt-2 sm:flex sm:justify-between">
                          <div className="sm:flex">
                            <p className="flex items-center text-sm text-gray-500">
                              <ArchiveBoxIcon className="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                              {item.category || 'Uncategorized'}
                            </p>
                          </div>
                          <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                            <ChartBarIcon className="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                            <p>
                              <time dateTime={item.createdAt}>{item.createdAtFormatted}</time>
                            </p>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </li>
                ))
              ) : (
                <li className="p-4 text-center text-gray-500">No recent items</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;